import { Icon } from '@iconify/react';
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import config from '../config';
import { useAuth } from '../context/AuthContext';

const SubscriptionPlansLayer = () => {
    const { auth } = useAuth();
    const [view, setView] = useState('list'); // 'list' or 'form'
    const [editingPlan, setEditingPlan] = useState(null);
    const [plans, setPlans] = useState([]);
    const [syncingId, setSyncingId] = useState(null);
    const [saving, setSaving] = useState(false);

    const fetchPlans = useCallback(async () => {
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/plans/all`);
            if (response.ok) {
                const data = await response.json();
                
                // Map API data to component state
                const mappedData = data.map(plan => {
                    let parsedFeatures = {};
                    try {
                        parsedFeatures = typeof plan.features === 'string' 
                            ? JSON.parse(plan.features) 
                            : (plan.features || {});
                    } catch (e) {
                        console.warn('Failed to parse features for plan', plan.id, e);
                        parsedFeatures = {};
                    }

                    return {
                        ...plan,
                        isActive: !!plan.is_active,
                        duration: plan.duration_type 
                            ? (plan.duration_type.charAt(0).toUpperCase() + plan.duration_type.slice(1)) 
                            : 'Monthly',
                        features: parsedFeatures
                    };
                });

                setPlans(mappedData);
            }
        } catch (error) {
            console.error('Error fetching plans:', error);
            toast.error('Failed to load plans');
        }
    }, [auth]);

    useEffect(() => {
        fetchPlans();
    }, [fetchPlans]);

    const handleEdit = (plan) => {
        setEditingPlan(plan);
        setView('form');
    };

    const handleCreate = () => {
        setEditingPlan(null);
        setView('form');
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        const formData = new FormData(e.target);
        
        const planData = {
            name: formData.get('name'),
            price: parseFloat(formData.get('price')),
            currency: 'AED',
            duration_type: formData.get('duration').toLowerCase(),
            duration_days: formData.get('duration') === 'Custom' ? 30 : (formData.get('duration') === 'Yearly' ? 365 : 30), // Simple logic
            is_active: formData.get('isActive') === 'on',
            max_venues: parseInt(formData.get('max_venues')),
            max_features: parseInt(formData.get('max_features')),
            features: {
                galleryLimit: parseInt(formData.get('galleryLimit')),
                rateOverview: formData.get('rateOverview') === 'on',
                blogs: parseInt(formData.get('blogs')),
                premiumColor: formData.get('premiumColor')
            }
        };

        try {
            const url = editingPlan 
                ? `${config.API_BASE_URL}/api/plans/${editingPlan.id}`
                : `${config.API_BASE_URL}/api/plans`;
            
            const method = editingPlan ? 'PUT' : 'POST';

            const response = await auth.fetch(url, {
                method: method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(planData)
            });

            const data = await response.json();

            if (response.ok) {
                toast.success(editingPlan ? 'Plan updated successfully' : 'Plan created successfully');
                fetchPlans();
                setView('list');
            } else {
                console.error('Failed to save plan', data);
                toast.error(data.error || 'Failed to save plan');
            }
        } catch (error) {
            console.error('Error saving plan:', error);
            toast.error('An error occurred while saving');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (plan) => {
        // Optimistic update
        const updatedPlans = plans.map(p => 
            p.id === plan.id ? { ...p, isActive: !p.isActive } : p
        );
        setPlans(updatedPlans);

        try {
            const payload = {
                name: plan.name,
                price: plan.price,
                currency: plan.currency || 'AED',
                duration_type: plan.duration_type || plan.duration?.toLowerCase() || 'monthly',
                duration_days: plan.duration_days || (plan.duration === 'Yearly' ? 365 : 30),
                is_active: !plan.isActive,
                max_venues: plan.max_venues || 1,
                max_features: plan.max_features || 5,
                features: plan.features || {},
                stripe_price_id: plan.stripe_price_id
            };

            const response = await auth.fetch(`${config.API_BASE_URL}/api/plans/${plan.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            
            if (!response.ok) {
                const data = await response.json();
                console.error('Failed to update status', data);
                toast.error(data.error || 'Failed to update status');
                fetchPlans(); // Revert
            }
        } catch (error) {
            console.error('Error updating status:', error);
            toast.error('Network error while updating status');
            fetchPlans(); // Revert
        }
    };

    const handleSyncStripe = async (plan) => {
        setSyncingId(plan.id);
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/plans/${plan.id}/sync-stripe`, {
                method: 'POST'
            });
            const data = await response.json();

            if (response.ok) {
                toast.success('Successfully synced with Stripe');
                fetchPlans();
            } else {
                console.error('Failed to sync with Stripe', data);
                toast.error(data.error || 'Failed to sync with Stripe');
            }
        } catch (error) {
            console.error('Error syncing with Stripe:', error);
            toast.error('Network error during Stripe sync');
        } finally {
            setSyncingId(null);
        }
    };

    if (view === 'form') {
        return (
            <div className="card h-100 p-0 radius-12">
                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between">
                    <h6 className="text-lg fw-semibold mb-0">{editingPlan ? 'Edit Plan' : 'Create New Plan'}</h6>
                    <button className="btn btn-secondary radius-8 px-20 py-11" onClick={() => setView('list')}>Cancel</button>
                </div>
                <div className="card-body p-24">
                    <form onSubmit={handleSave}>
                        <div className="row gy-4">
                            {/* Basic Info */}
                            <div className="col-md-6">
                                <label className="form-label fw-semibold text-primary-light text-sm mb-8">Plan Name</label>
                                <input type="text" name="name" className="form-control radius-8" defaultValue={editingPlan?.name} required placeholder="e.g. Gold Package" />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold text-primary-light text-sm mb-8">Price (AED)</label>
                                <input type="number" step="0.01" name="price" className="form-control radius-8" defaultValue={editingPlan?.price} required placeholder="0.00" />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold text-primary-light text-sm mb-8">Duration</label>
                                <select name="duration" className="form-control radius-8" defaultValue={editingPlan?.duration_type ? (editingPlan.duration_type.charAt(0).toUpperCase() + editingPlan.duration_type.slice(1)) : 'Monthly'}>
                                    <option value="Monthly">Monthly</option>
                                    <option value="Yearly">Yearly</option>
                                    <option value="Custom">Custom Days</option>
                                </select>
                            </div>
                            <div className="col-md-6 d-flex align-items-end">
                                <div className="form-check style-check d-flex align-items-center">
                                    <input className="form-check-input border-gray-200" type="checkbox" name="isActive" id="isActive" defaultChecked={editingPlan?.is_active ?? true} />
                                    <label className="form-check-label fw-medium text-secondary-light ms-2" htmlFor="isActive">
                                        Plan is Active (Available for purchase)
                                    </label>
                                </div>
                            </div>

                            <div className="col-12"><hr className="my-0" /></div>
                            <div className="col-12"><h6 className="text-md fw-bold mb-0">Plan Features & Limits</h6></div>

                            {/* Features */}
                            <div className="col-md-6">
                                <label className="form-label fw-semibold text-primary-light text-sm mb-8">Max Venues (Listing Limit)</label>
                                <input type="number" name="max_venues" className="form-control radius-8" defaultValue={editingPlan?.max_venues ?? editingPlan?.features?.listingLimit ?? 1} required />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold text-primary-light text-sm mb-8">Max Features per Venue</label>
                                <input type="number" name="max_features" className="form-control radius-8" defaultValue={editingPlan?.max_features ?? 5} required />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold text-primary-light text-sm mb-8">Gallery Images Limit</label>
                                <input type="number" name="galleryLimit" className="form-control radius-8" defaultValue={editingPlan?.features?.galleryLimit ?? 5} required />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold text-primary-light text-sm mb-8">Premium Card Color</label>
                                <div className="d-flex align-items-center gap-2">
                                    <input type="color" name="premiumColor" className="form-control form-control-color radius-8" defaultValue={editingPlan?.features?.premiumColor || '#487fff'} title="Choose your color" />
                                    <span className="text-sm text-secondary-light">Hex Code Color for UI Badges</span>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold text-primary-light text-sm mb-8">Blog Posts Limit</label>
                                <input type="number" name="blogs" className="form-control radius-8" defaultValue={editingPlan?.features?.blogs === true ? 5 : (Number.isInteger(editingPlan?.features?.blogs) ? editingPlan?.features?.blogs : 0)} placeholder="0 for disabled" required />
                                <span className="text-xs text-secondary-light">Enter 0 to disable blogs for this plan</span>
                            </div>
                            <div className="col-md-6 d-flex align-items-center gap-4 mt-auto">
                                <div className="form-check style-check d-flex align-items-center">
                                    <input className="form-check-input border-gray-200" type="checkbox" name="rateOverview" id="rateOverview" defaultChecked={editingPlan?.features?.rateOverview} />
                                    <label className="form-check-label fw-medium text-secondary-light ms-2" htmlFor="rateOverview">
                                        Show Rate Overview
                                    </label>
                                </div>
                            </div>

                            <div className="col-12 mt-24">
                                <button type="submit" className="btn btn-primary radius-8 px-20 py-11" disabled={saving}>
                                    {saving ? 'Saving...' : 'Save Plan'}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between">
                <h6 className="text-lg fw-semibold mb-0">Subscription Plans Configuration</h6>
                <button className="btn btn-primary radius-8 px-20 py-11 d-flex align-items-center gap-2" onClick={handleCreate}>
                    <Icon icon="ic:baseline-plus" className="text-xl" /> Create New Plan
                </button>
            </div>
            <div className="card-body p-24">
                <div className="table-responsive scroll-sm">
                    <table className="table bordered-table sm-table mb-0">
                        <thead>
                            <tr>
                                <th scope="col">Plan Name</th>
                                <th scope="col">Price</th>
                                <th scope="col">Duration</th>
                                <th scope="col">Status</th>
                                <th scope="col">Key Features</th>
                                <th scope="col" className="text-center">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {plans.map(plan => (
                                <tr key={plan.id}>
                                    <td>
                                        <h6 className="text-md mb-0 fw-medium">{plan.name}</h6>
                                    </td>
                                    <td>AED {plan.price}</td>
                                    <td>{plan.duration}</td>
                                    <td>
                                        <div className={`form-check form-switch d-flex align-items-center ${plan.isActive ? 'switch-success' : ''}`}>
                                            <input 
                                                className="form-check-input" 
                                                type="checkbox" 
                                                role="switch" 
                                                checked={plan.isActive}
                                                onChange={() => handleToggleActive(plan)}
                                                style={{ cursor: 'pointer' }}
                                            />
                                            <label className={`form-check-label ms-2 text-sm fw-medium ${plan.isActive ? 'text-success-main' : 'text-secondary-light'}`}>
                                                {plan.isActive ? 'Active' : 'Inactive'}
                                            </label>
                                        </div>
                                    </td>
                                    <td>
                                        <div className="d-flex flex-column gap-1">
                                            <span className="text-xs text-secondary-light">Limit: {plan.max_venues ?? plan.features?.listingLimit ?? 'N/A'} Listings</span>
                                            <span className="text-xs text-secondary-light">Gallery: {plan.features?.galleryLimit ?? 0} Images</span>
                                            <span className="text-xs text-secondary-light">Blogs: {plan.features?.blogs > 0 ? plan.features.blogs : 'Disabled'}</span>
                                            <div className="d-flex gap-1 align-items-center flex-wrap">
                                                {plan.features?.rateOverview && <span className="badge bg-info-focus text-info-main text-xs px-8">Rates</span>}
                                                {plan.features?.premiumColor && <span className="badge text-xs px-8 text-white" style={{backgroundColor: plan.features.premiumColor}}>Color</span>}
                                                
                                                {plan.stripe_price_id ? (
                                                    <span className="badge bg-success-focus text-success-main text-xs px-8" title={`Stripe ID: ${plan.stripe_price_id}`}>Synced</span>
                                                ) : (
                                                    <button 
                                                        onClick={() => handleSyncStripe(plan)} 
                                                        className="badge bg-warning-focus text-warning-main text-xs px-8 border-0" 
                                                        title="Click to sync with Stripe"
                                                        disabled={syncingId === plan.id}
                                                    >
                                                        {syncingId === plan.id ? 'Syncing...' : 'Not Synced'} <Icon icon={syncingId === plan.id ? "eos-icons:loading" : "ic:baseline-refresh"} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="text-center">
                                        <button onClick={() => handleEdit(plan)} className="btn btn-sm btn-outline-primary radius-8 px-14 py-6">
                                            Edit
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default SubscriptionPlansLayer;